/**
 * Support Reports Dashboard
 *
 * Analytics and reporting interface for support ticket system.
 * Displays KPIs, charts, and performance metrics.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 3: Analytics + Bulk Actions
 * Location: apps/web/src/pages/support/SupportReports.jsx
 *
 * Features:
 * - Overview KPI cards (volume, SLA compliance, avg resolution time)
 * - Daily metrics line chart
 * - Staff performance table
 * - Request type breakdown
 * - Hourly trends heatmap
 * - Resolution time distribution
 * - Period selector (7d, 30d, 90d)
 * - Manual refresh button
 */

import React, { useState } from 'react';
import { useNavigate, useLoaderData } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp as _TrendingUp,
  Users as _Users,
  Clock,
  CheckCircle as _CheckCircle,
  AlertCircle as _AlertCircle,
  RefreshCw,
  Calendar as _Calendar,
  TrendingUp,
  Users,
  AlertCircle
} from 'lucide-react';
import { useSupportAnalytics } from '../../hooks/support/useSupportAnalytics';
import OverviewCards from '../../components/support/analytics/OverviewCards';
import DailyMetricsChart from '../../components/support/analytics/DailyMetricsChart';
import StaffPerformanceTable from '../../components/support/analytics/StaffPerformanceTable';
import TypeBreakdownChart from '../../components/support/analytics/TypeBreakdownChart';
import ResolutionTimeChart from '../../components/support/analytics/ResolutionTimeChart';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function SupportReports() {
  const navigate = useNavigate();
  const loaderData = useLoaderData();
  const user = loaderData?.user || { name: 'User', email: 'user@example.com', avatar: null };

  // Determine user role - admins access via admin dashboard, support staff may have different role
  const userRole = user.roles?.includes('super_admin') || user.roles?.includes('staff') ? 'admin' : 'support';
  const activeNavId = userRole === 'admin' ? 'support' : 'reports';

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const {
    overview,
    dailyMetrics,
    staffMetrics,
    typeBreakdown,
    resolutionTime,
    lastRefresh,
    loading,
    error,
    refresh: _refresh,
    manualRefresh,
  } = useSupportAnalytics(selectedPeriod);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await manualRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const periods = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
  ];

  if (error) {
    return (
      <DashboardLayout
        userRole={userRole}
        activeNavId={activeNavId}
        onNavigate={handleNavigate}
        user={user}
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">Failed to load analytics</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole={userRole}
      activeNavId={activeNavId}
      onNavigate={handleNavigate}
      user={user}
    >
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Ticket volume, SLA compliance, and team performance metrics
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {periods.map((period) => (
              <button
                key={period.value}
                onClick={() => setSelectedPeriod(period.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedPeriod === period.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Last Refresh Time */}
      {lastRefresh && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Clock className="w-3.5 h-3.5" />
          <span>Last updated: {new Date(lastRefresh).toLocaleString()}</span>
        </div>
      )}

      {loading && !overview ? (
        /* Initial Loading State */
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6 h-96 animate-pulse"></div>
        </div>
      ) : (
        <>
          {/* Overview KPI Cards */}
          <OverviewCards overview={overview} />

          {/* Daily Metrics Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Daily Ticket Volume</h2>
                <p className="text-sm text-gray-500 mt-1">
                  New tickets, resolved, and pending over time
                </p>
              </div>
              <TrendingUp className="w-6 h-6 text-gray-400" />
            </div>
            <DailyMetricsChart dailyMetrics={dailyMetrics} />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Type Breakdown */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Tickets by Type & Priority
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Volume distribution across categories
                  </p>
                </div>
                <BarChart3 className="w-6 h-6 text-gray-400" />
              </div>
              <TypeBreakdownChart typeBreakdown={typeBreakdown} />
            </div>

            {/* Resolution Time Distribution */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Resolution Time</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Distribution of time to resolve tickets
                  </p>
                </div>
                <Clock className="w-6 h-6 text-gray-400" />
              </div>
              <ResolutionTimeChart resolutionTime={resolutionTime} />
            </div>
          </div>

          {/* Staff Performance Table */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Staff Performance</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Individual metrics for last 30 days
                </p>
              </div>
              <Users className="w-6 h-6 text-gray-400" />
            </div>
            <StaffPerformanceTable staffMetrics={staffMetrics} />
          </div>
        </>
      )}
      </div>
    </DashboardLayout>
  );
}
